import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Mail, MapPin, Phone } from 'lucide-react';
import axios from 'axios';
import CookiePreferences from './CookiePreferences';
import WhatsAppSupportLink from './WhatsAppSupportLink';
import SocialLinks from './SocialLinks';
import { useSiteContent } from '../hooks/useSiteContent';
import { BACKEND_URL } from '../config/api';
import { COMPANY, COMPANY_LEGAL_FOOTER_LONG, COMPANY_LEGAL_FOOTER_SHORT, copyrightLine } from '../config/company';

export default function Footer({ minimal = false }) {
  const navigate = useNavigate();
  const { get } = useSiteContent();
  const [showCookiePrefs, setShowCookiePrefs] = useState(false);
  const [waConfig, setWaConfig] = useState(null);
  const [user, setUser] = useState(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/settings/public`);
        const wa = res.data?.whatsapp_support;
        if (wa?.enabled) setWaConfig(wa);
      } catch {}
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {}
    })();
  }, []);

  if (minimal) {
    return (
      <footer className="bg-slate-100 border-t border-slate-200 py-4 px-4" data-testid="site-footer-minimal">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span data-testid="footer-copyright-minimal">{copyrightLine(currentYear)}</span>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
            <Link to="/cookies" className="hover:text-slate-700">Cookies</Link>
            <button onClick={() => setShowCookiePrefs(true)} className="hover:text-slate-700">Cookie Settings</button>
          </div>
        </div>
        <p className="max-w-7xl mx-auto mt-2 text-[11px] text-slate-500 text-center sm:text-left" data-testid="footer-company-info-minimal">
          {COMPANY_LEGAL_FOOTER_SHORT}
        </p>
        <CookiePreferences open={showCookiePrefs} onClose={() => setShowCookiePrefs(false)} />
      </footer>
    );
  }

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-10">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-7 w-7 text-orange-500" />
              <span className="text-xl font-bold">OnsiteHand</span>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              {get('footer.brand.tagline', 'Connecting employers with skilled tradespeople across the UK.')}
            </p>
            <div className="space-y-2 text-sm text-slate-400">
              <a href={`mailto:${get('footer.brand.email', 'support@onsitehand.co.uk')}`} className="flex items-center gap-2 hover:text-white">
                <Mail className="h-4 w-4" />
                {get('footer.brand.email', 'support@onsitehand.co.uk')}
              </a>
            </div>
            <SocialLinks className="mt-4" />
          </div>

          {/* For Employers */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">{get('footer.employer.heading', 'For Employers')}</h4>
            <ul className="space-y-2.5 text-slate-400 text-sm">
              <li>
                <button onClick={() => navigate('/register?type=employer')} className="hover:text-white transition-colors">
                  Sign Up
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/search')} className="hover:text-white transition-colors">
                  Find Tradespeople
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/faq')} className="hover:text-white transition-colors">
                  FAQ
                </button>
              </li>
            </ul>
          </div>

          {/* For Tradespeople */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">{get('footer.trades.heading', 'For Tradespeople')}</h4>
            <ul className="space-y-2.5 text-slate-400 text-sm">
              <li>
                <button onClick={() => navigate('/register?type=tradesperson')} className="hover:text-white transition-colors">
                  Sign Up
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                  Login
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/faq')} className="hover:text-white transition-colors">
                  FAQ
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">{get('footer.legal.heading', 'Legal')}</h4>
            <ul className="space-y-2.5 text-slate-400 text-sm">
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
              </li>

            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">{get('footer.support.heading', 'Support')}</h4>
            <ul className="space-y-2.5 text-slate-400 text-sm">
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
              </li>
              <li>
                <Link to="/help" className="hover:text-white transition-colors">Help Centre</Link>
              </li>
              <li>
                <button onClick={() => setShowCookiePrefs(true)} className="hover:text-white transition-colors">
                  Cookie Preferences
                </button>
              </li>
              <li>
                <Link to="/report-bug" className="hover:text-white transition-colors" data-testid="footer-report-bug">Report a Bug</Link>
              </li>
              {waConfig && (
                <li>
                  <WhatsAppSupportLink waConfig={waConfig} user={user} label="WhatsApp Support" variant="footer" />
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-slate-400 text-sm text-center md:text-left" data-testid="footer-legal-block">
              <p data-testid="footer-copyright">{copyrightLine(currentYear)}</p>
              <p className="text-xs text-slate-500 mt-2 max-w-3xl leading-relaxed" data-testid="footer-company-info">
                {get('footer.company_info', COMPANY_LEGAL_FOOTER_LONG)}
              </p>
              <p className="text-[11px] text-slate-600 mt-1">
                Registered office only. Please contact us online for support.
              </p>
              <p className="text-[11px] text-slate-600 mt-1">
                <a
                  href="https://find-and-update.company-information.service.gov.uk/company/17186065"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-slate-300"
                  data-testid="footer-companies-house-link"
                >
                  View our Companies House registration
                </a>
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <Link to="/terms" className="hover:text-slate-300">Terms</Link>
              <Link to="/privacy" className="hover:text-slate-300">Privacy</Link>
              <Link to="/cookies" className="hover:text-slate-300">Cookies</Link>
              <button onClick={() => setShowCookiePrefs(true)} className="hover:text-slate-300">
                Cookie Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      <CookiePreferences open={showCookiePrefs} onClose={() => setShowCookiePrefs(false)} />
    </footer>
  );
}
